import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { HexColorPicker } from "react-colorful";
import {
  getColorLabel,
  normalizeColorValue,
  resolveColorHex,
} from "../utils/teamColors";

interface TeamColorPickerProps {
  color: string;
  palette: string[];
  usedColors: Set<string>;
  onChange: (color: string) => void;
  disabled?: boolean;
}

export function TeamColorPicker({
  color,
  palette,
  usedColors,
  onChange,
  disabled,
}: TeamColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedColor = normalizeColorValue(color);
  const colorHex = resolveColorHex(color);
  const colorLabel = color ? getColorLabel(color) : "Select a color";

  const paletteOptions = useMemo(() => {
    const uniquePalette = Array.from(new Set(palette));
    return uniquePalette
      .map((paletteColor) => {
        const normalized = normalizeColorValue(paletteColor);
        return {
          color: paletteColor,
          label: getColorLabel(paletteColor),
          normalized,
          hex: resolveColorHex(paletteColor),
          isSelected: normalized !== "" && normalized === selectedColor,
          isUsed: usedColors.has(normalized),
        };
      })
      .filter((option) => !option.isUsed || option.isSelected);
  }, [palette, selectedColor, usedColors]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={disabled}
        className="flex w-full items-center justify-between gap-3 rounded-md border border-gray-300 bg-white px-3 py-2 text-left shadow-sm hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-50"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span className="flex items-center gap-3">
          <span
            className="h-8 w-8 rounded border border-gray-200"
            style={{ backgroundColor: colorHex }}
          />
          <span>
            <span className="block text-sm font-medium text-gray-900">
              {colorLabel}
            </span>
          </span>
        </span>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 z-30 mt-2 w-80 rounded-md border border-gray-200 bg-white p-3 shadow-lg">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-500">
              Available colors
            </p>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Close
            </button>
          </div>

          {paletteOptions.length > 0 ? (
            <div className="mt-2 grid grid-cols-6 gap-2">
              {paletteOptions.map((option) => (
                <button
                  key={option.color}
                  type="button"
                  onClick={() => {
                    onChange(option.color);
                    setIsOpen(false);
                  }}
                  className={`flex h-9 w-9 items-center justify-center rounded border ${
                    option.isSelected
                      ? "border-blue-500 ring-2 ring-blue-200"
                      : "border-gray-200 hover:border-blue-400"
                  }`}
                  title={option.label}
                >
                  <span
                    className="h-5 w-5 rounded"
                    style={{ backgroundColor: option.hex }}
                  />
                </button>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-xs text-gray-500">
              No palette colors available. Choose a custom color below.
            </p>
          )}

          <div className="mt-3 border-t border-gray-100 pt-3">
            <p className="text-xs font-semibold text-gray-500">Custom color</p>
            <HexColorPicker
              color={colorHex}
              onChange={onChange}
              className="mt-2 h-36 w-full"
            />
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
              <span
                className="h-4 w-4 rounded border border-gray-200"
                style={{ backgroundColor: colorHex }}
              />
              <span>{color ? getColorLabel(color) : "No color selected"}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
